(() => {
  if (customElements.get('cp-collection-search')) return;
  const normalize = value => value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  class CollectionSearch extends HTMLElement {
    connectedCallback() {
      if (!this.ready) {
        this.ready = true;
        this.query = this.querySelector('[data-query]');
        this.type = this.querySelector('[data-type]');
        this.sort = this.querySelector('[data-sort]');
        this.stock = this.querySelector('[data-stock]');
        this.status = this.querySelector('[data-status]');
        this.results = this.querySelector('[data-results]');
        this.grid = this.querySelector('[data-grid]');
        this.filters = this.querySelector('[data-filters]');
        this.filterToggle = this.querySelector('[data-filters-toggle]');
        this.filterCount = this.querySelector('[data-filter-count]');
        this.clearButton = this.querySelector('[data-clear]');
        this.filterToggle.addEventListener('click', () => {
          this.filters.hidden = !this.filters.hidden;
          this.filterToggle.setAttribute('aria-expanded', String(!this.filters.hidden));
        });
        this.query.addEventListener('input', () => {
          this.syncControls();
          clearTimeout(this.timer);
          this.timer = setTimeout(() => this.update(), 180);
        });
        [this.type, this.sort, this.stock].forEach(control => control.addEventListener('change', () => this.update()));
        this.clearButton.addEventListener('click', () => {
          clearTimeout(this.timer);
          this.query.value = ''; this.type.value = ''; this.sort.value = 'default'; this.stock.checked = false;
          this.update(); this.query.focus();
        });
      }
      this.hidden = false;
      this.syncControls();
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.pending = null;
    }
    active() {
      return Boolean(this.query.value.trim() || this.type.value || this.stock.checked || this.sort.value !== 'default');
    }
    syncControls() {
      const active = this.active();
      this.clearButton.hidden = !active;
      this.filterToggle.hidden = !active;
      if (!active) {
        this.filters.hidden = true;
        this.filterToggle.setAttribute('aria-expanded', 'false');
      }
      const count = Number(Boolean(this.type.value)) + Number(this.stock.checked) + Number(this.sort.value !== 'default');
      this.filterCount.textContent = count ? ` (${count})` : '';
    }
    async load() {
      if (this.items) return this.items;
      if (this.pending) return this.pending;
      const controller = new AbortController();
      this.controller = controller;
      const task = (async () => {
        const endpoint = new URL(this.dataset.url, location.origin);
        endpoint.searchParams.set('view', 'cp-search-index');
        const current = new URL(location.href);
        ['sort_by', 'preview_theme_id'].forEach(key => {
          if (current.searchParams.has(key)) endpoint.searchParams.set(key, current.searchParams.get(key));
        });
        const items = new Map();
        const visited = new Set();
        let next = endpoint.href;
        let total = null;
        while (next) {
          if (visited.has(next) || visited.size >= 250) throw new Error('Incomplete collection index');
          visited.add(next);
          const response = await fetch(next, { signal: controller.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Collection request failed');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const index = doc.querySelector('[data-cp-search-index]');
          if (!index) throw new Error('Collection search template is missing');
          total = Number(index.dataset.total);
          index.querySelectorAll('[data-cp-search-item]').forEach(node => {
            items.set(node.dataset.id, { node, words: normalize(node.dataset.keywords), title: node.dataset.title,
              type: node.dataset.type, tags: JSON.parse(node.dataset.tags || '[]'), available: node.dataset.available === 'true', price: Number(node.dataset.price) });
          });
          next = null;
          if (index.dataset.next) {
            const page = new URL(index.dataset.next, endpoint);
            if (page.origin !== location.origin || page.pathname !== endpoint.pathname) throw new Error('Invalid collection page');
            page.searchParams.set('view', 'cp-search-index');
            ['sort_by', 'preview_theme_id'].forEach(key => {
              if (endpoint.searchParams.has(key)) page.searchParams.set(key, endpoint.searchParams.get(key));
            });
            next = page.href;
          }
        }
        if (!Number.isFinite(total) || items.size !== total) throw new Error('Incomplete collection index');
        this.items = [...items.values()];
        return this.items;
      })();
      this.pending = task;
      try { return await task; }
      finally { if (this.pending === task) this.pending = null; }
    }
    async update() {
      this.syncControls();
      // Resolve the original grid each time: Shopify facets can replace its HTML.
      const original = this.parentElement?.querySelector('.collection-wrapper');
      if (!original) return;
      if (!this.active()) {
        original.hidden = false; this.results.hidden = true; this.status.textContent = '';
        this.removeAttribute('aria-busy'); return;
      }
      this.status.textContent = 'Searching the entire collection…';
      this.setAttribute('aria-busy', 'true');
      try {
        const items = await this.load();
        if (!this.isConnected || !this.active()) return;
        const terms = normalize(this.query.value).split(' ').filter(Boolean);
        const category = this.type.value;
        const categoryMatches = item => !category || (category.startsWith('type:') ? item.type === category.slice(5) : item.tags.includes(category.slice(4)));
        let matches = items.filter(item => terms.every(term => item.words.includes(term)) &&
          categoryMatches(item) && (!this.stock.checked || item.available));
        if (this.sort.value === 'title') matches.sort((a, b) => a.title.localeCompare(b.title));
        if (this.sort.value === 'price-up') matches.sort((a, b) => a.price - b.price);
        if (this.sort.value === 'price-down') matches.sort((a, b) => b.price - a.price);
        this.grid.replaceChildren(...matches.map(item => item.node.cloneNode(true)));
        original.hidden = true; this.results.hidden = false;
        this.status.textContent = matches.length ? `${matches.length} matching ${matches.length === 1 ? 'product' : 'products'} in this collection.` : 'No matching products. Try another name or part number, or clear the filters.';
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected && this.active()) {
          original.hidden = false; this.results.hidden = true;
          this.status.textContent = 'Search could not load. Please try again or use the collection filters below.';
        }
      } finally { this.removeAttribute('aria-busy'); }
    }
  }
  customElements.define('cp-collection-search', CollectionSearch);
})();
